/**
 * DocumentManagement - Archive of the user's fully signed transaction documents.
 *
 * Read-only by design: documents are generated, sent and signed where the
 * transaction happens (the Negotiation workspace's DocumentFlowPanel for offer
 * documents, the listing flow for the Listing Agreement). This page only lists
 * completed documents so users can view, download, and pull the DocuSign
 * Certificate of Completion. Anything still awaiting the user's signature is
 * surfaced as a banner that points back to where it gets signed.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import DocumentAPI, { useDocuments } from '../../agents/documentAgent';
import { useAuth } from '../../agents/authAgent';
import { fetchCertificate } from '../../agents/docusignAgent';
import { documentDisplayName, formatDocType, signerRoleLabel } from '../../utils/documentTypes';
import './DocumentManagement.css';

const PER_PAGE = 20;
// Upper bound for the pending-signature scan; the list endpoint caps per_page at 100.
const PENDING_SCAN_LIMIT = 100;

// Labels live in utils/documentTypes (shared with the document flow panel);
// re-exported here for existing importers.
export { formatDocType };

const isSameUserId = (left, right) => left != null && right != null && Number(left) === Number(right);

// Every role reads as itself: a buyer's spouse (e.g. on the Buyer's
// Acknowledgment of Receipt) is not "Seller".
const signerLabel = (sig) => sig.signer_name || signerRoleLabel(sig.signer_role);

// QA run 2026-10-19 (D3/S18): a lead-paint disclosure rendered before the form
// had signature lines rode a completed envelope, but nobody signed the form.
export const UNSIGNED_FORM_NOTE =
  'This copy of the form has no signature lines, so it was never signed, even though its envelope was completed. A signed Lead-Based Paint Disclosure is still required. Contact cutthe6.com support.';

const formatDate = (value) => (value ? new Date(value).toLocaleDateString() : null);

/**
 * Documents still waiting on the current user's signature.
 *
 * @param {Array} documents - Unfiltered document list.
 * @param {number|string|null} userId - Current user id.
 * @returns {Array} Documents where the user's signature row is pending.
 */
export const pendingForUser = (documents, userId) => {
  if (!Array.isArray(documents) || userId == null) return [];
  return documents.filter((doc) => {
    if (doc.status === 'completed' || doc.status === 'voided') return false;
    const signatures = Array.isArray(doc.signatures) ? doc.signatures : [];
    return signatures.some((sig) => isSameUserId(sig.signer_id, userId) && sig.status === 'pending');
  });
};

const DocumentManagement = ({ onNavigateToNegotiations, onNavigateToListings }) => {
  const { user: currentUser } = useAuth();
  const currentUserId = currentUser?.id ?? null;

  const {
    documents,
    loading,
    error,
    total,
    page,
    fetchDocuments,
    downloadDocument,
  } = useDocuments();

  const [pendingDocs, setPendingDocs] = useState([]);
  const [previewDoc, setPreviewDoc] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  const [actionFeedback, setActionFeedback] = useState(null);
  const feedbackTimerRef = useRef(null);

  useEffect(() => {
    fetchDocuments(1, 'completed');
  }, [fetchDocuments]);

  // Best-effort scan for documents awaiting the user's signature. A failure
  // here only hides the banner; it must not block the archive.
  useEffect(() => {
    if (currentUserId == null) return undefined;
    let cancelled = false;
    DocumentAPI.listDocuments(null, null, 1, PENDING_SCAN_LIMIT)
      .then((data) => {
        if (!cancelled) setPendingDocs(pendingForUser(data?.documents, currentUserId));
      })
      .catch(() => {
        if (!cancelled) setPendingDocs([]);
      });
    return () => { cancelled = true; };
  }, [currentUserId]);

  // Release the preview's blob URL when it changes or the page unmounts.
  useEffect(() => () => {
    if (previewUrl) window.URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  useEffect(() => () => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
  }, []);

  const showFeedback = useCallback((message, type = 'success') => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    setActionFeedback({ message, type });
    feedbackTimerRef.current = setTimeout(() => setActionFeedback(null), 4000);
  }, []);

  const handleView = async (doc) => {
    setPreviewDoc(doc);
    setPreviewUrl(null);
    setPreviewError(false);
    setPreviewLoading(true);
    try {
      const blob = await DocumentAPI.fetchDocumentPdf(doc.id);
      setPreviewUrl(window.URL.createObjectURL(blob));
    } catch {
      setPreviewError(true);
    } finally {
      setPreviewLoading(false);
    }
  };

  const handleBack = () => {
    setPreviewDoc(null);
    setPreviewUrl(null);
    setPreviewError(false);
  };

  const handleDownload = async (docId) => {
    try {
      await downloadDocument(docId);
      showFeedback('Download started');
    } catch {
      showFeedback('Failed to download document', 'error');
    }
  };

  const handleCertificate = async (docId) => {
    try {
      await fetchCertificate(docId);
      showFeedback('Certificate downloaded');
    } catch (err) {
      showFeedback(err?.message || 'Failed to download certificate', 'error');
    }
  };

  // --- Render helpers ---

  const renderFeedback = () => {
    if (!actionFeedback) return null;
    return (
      <div className={`doc-feedback doc-feedback-${actionFeedback.type}`} role="status">
        {actionFeedback.message}
      </div>
    );
  };

  const renderPendingBanner = () => {
    if (pendingDocs.length === 0) return null;
    const count = pendingDocs.length;
    const hasOfferDocs = pendingDocs.some((d) => d.offer_id != null);
    const hasListingDocs = pendingDocs.some((d) => d.offer_id == null && d.house_id != null);
    return (
      <div className="doc-pending-banner" role="status">
        <span className="doc-pending-banner-icon" aria-hidden="true">✍️</span>
        <div className="doc-pending-banner-body">
          <strong>
            {count} document{count === 1 ? ' is' : 's are'} waiting for your signature
          </strong>
          <span>Signed copies appear here once every party has signed.</span>
        </div>
        <div className="doc-pending-banner-actions">
          {hasOfferDocs && onNavigateToNegotiations && (
            <button type="button" className="doc-btn doc-btn-sm doc-btn-primary" onClick={() => onNavigateToNegotiations()}>
              Go to Negotiations
            </button>
          )}
          {hasListingDocs && onNavigateToListings && (
            <button type="button" className="doc-btn doc-btn-sm doc-btn-primary" onClick={() => onNavigateToListings()}>
              Go to My Listings
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDocCard = (doc) => {
    const signedOn = formatDate(doc.completed_at || doc.updated_at);
    const signers = (Array.isArray(doc.signatures) ? doc.signatures : []).map(signerLabel);
    const unsignedForm = Boolean(doc.signature_fields_missing);
    return (
      <div key={doc.id} className="doc-card">
        <div className="doc-card-header">
          <h4 className="doc-card-title">{documentDisplayName(doc)}</h4>
          {unsignedForm ? (
            <span className="doc-status-badge doc-status-error">Not signed</span>
          ) : (
            <span className="doc-status-badge doc-status-completed">Signed</span>
          )}
        </div>
        <div className="doc-card-meta">
          <span>{formatDocType(doc.document_type)}</span>
          {signedOn && <span>{unsignedForm ? 'Envelope completed' : 'Signed'} {signedOn}</span>}
        </div>
        {unsignedForm ? (
          <p className="doc-card-signers" role="note">{UNSIGNED_FORM_NOTE}</p>
        ) : signers.length > 0 && (
          <p className="doc-card-signers">Signed by {signers.join(', ')}</p>
        )}
        <div className="doc-card-actions">
          <button type="button" className="doc-btn doc-btn-sm doc-btn-view" onClick={() => handleView(doc)}>
            View
          </button>
          <button type="button" className="doc-btn doc-btn-sm" onClick={() => handleDownload(doc.id)}>
            Download PDF
          </button>
          {doc.esign_provider === 'docusign' && (
            <button type="button" className="doc-btn doc-btn-sm" onClick={() => handleCertificate(doc.id)}>
              Certificate
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderDocumentList = () => {
    if (documents.length === 0) {
      return (
        <div className="doc-empty-state">
          <span className="doc-empty-icon" aria-hidden="true">📜</span>
          <h3>No signed documents yet</h3>
          <p>Once every party has signed a document, the signed copy is kept here.</p>
        </div>
      );
    }

    const groups = documents.reduce((acc, doc) => {
      const key = doc.property_address || 'Other documents';
      if (!acc[key]) acc[key] = [];
      acc[key].push(doc);
      return acc;
    }, {});
    const groupKeys = Object.keys(groups).sort();

    return groupKeys.map((key) => (
      <div key={key} className="doc-group">
        <h3 className="doc-group-heading">
          <span className="doc-group-icon" aria-hidden="true">🏠</span>
          {key}
          <span className="doc-group-count">({groups[key].length})</span>
        </h3>
        <div className="doc-list">
          {groups[key].map((doc) => renderDocCard(doc))}
        </div>
      </div>
    ));
  };

  const renderPreview = () => (
    <div className="doc-preview">
      <div className="doc-preview-toolbar">
        <button type="button" className="doc-btn doc-btn-back" onClick={handleBack}>
          ← Back to Signed Documents
        </button>
        <h2 className="doc-preview-title">{previewDoc.title}</h2>
        <button type="button" className="doc-btn doc-btn-primary" onClick={() => handleDownload(previewDoc.id)}>
          Download PDF
        </button>
      </div>
      {previewLoading && <div className="doc-preview-empty"><p>Loading signed PDF…</p></div>}
      {previewError && (
        <div className="doc-preview-empty">
          <p>Couldn&apos;t load the signed PDF.</p>
          <p>Try downloading it instead.</p>
        </div>
      )}
      {previewUrl && (
        <iframe
          className="doc-preview-frame"
          src={previewUrl}
          title={`Signed copy: ${previewDoc.title}`}
        />
      )}
    </div>
  );

  const renderPagination = () => {
    if (total <= PER_PAGE) return null;
    return (
      <div className="doc-pagination">
        <button
          type="button"
          className="doc-btn"
          onClick={() => fetchDocuments(page - 1, 'completed')}
          disabled={page <= 1}
        >
          Prev
        </button>
        <span>Page {page}</span>
        <button
          type="button"
          className="doc-btn"
          onClick={() => fetchDocuments(page + 1, 'completed')}
          disabled={page * PER_PAGE >= total}
        >
          Next
        </button>
      </div>
    );
  };

  return (
    <div className="doc-management">
      <div className="doc-page-header">
        <div className="doc-page-title-row">
          <div>
            <h1>Signed Documents</h1>
            <p className="doc-page-subtitle">Fully signed copies of your transaction documents</p>
          </div>
        </div>
      </div>

      {renderFeedback()}
      {error && <div className="doc-error">{error}</div>}

      {previewDoc ? renderPreview() : (
        <>
          {renderPendingBanner()}
          {loading ? (
            <div className="doc-skeleton-list">
              {[1, 2, 3].map((i) => (
                <div key={i} className="doc-skeleton-card">
                  <div className="doc-skeleton-line doc-skeleton-line--title" />
                  <div className="doc-skeleton-line" />
                  <div className="doc-skeleton-line doc-skeleton-line--short" />
                </div>
              ))}
            </div>
          ) : (
            renderDocumentList()
          )}
          {renderPagination()}
        </>
      )}
    </div>
  );
};

DocumentManagement.propTypes = {
  onNavigateToNegotiations: PropTypes.func,
  onNavigateToListings: PropTypes.func,
};

export default DocumentManagement;
